import { Plugin, FormData, AnyFormElement, FormField } from '../forms-ui-form-faas.interface';

declare global { interface Window { _zi_fc: any; } }
window._zi_fc = window._zi_fc || {};

/**
 * tests whether zoomInfo plugin is present in payload
 * returns either null or plugin data
 * previously "checkZoomInfoEnabled"
 */
export const getZoomInfoPlugin = (formData: FormData): Plugin | null => {
	/** test if 'plugin' section is missing, isn't an array or if its length is 0 */
	if (!formData.plugins || !Array.isArray(formData.plugins) || formData.plugins.length === 0) return null;

	/** find plugin called 'ZOOMINFO' */
	const ziPlugins = formData.plugins.filter((plugin) => (plugin.name === 'ZOOMINFO' && plugin.enabled));

	/** test if no ZOOMINFO plugins were found */
	if (ziPlugins.length === 0) return null;

	/** return first instance of ZOOMINFO */
	return ziPlugins[0];
};

/**
 * takes in list of formfield refs and hides each one
 */
export const hideFields = (formFields: AnyFormElement[]): void => {
	formFields.forEach((formField) => {
		formField.style.display = 'none';
	});
};

/**
 * takes in list of formfield refs and SHOWS each one
 */
export const showFields = (formFields: AnyFormElement[]): void => {
	formFields.forEach((formField) => {
		formField.style.display = 'block';
	});
};

/** zoominfo callback to show unmatched fields */
export const onMatch = (ziFields: any, formFieldRefs: AnyFormElement[]): void => {
	const fieldsToShow = formFieldRefs.filter(
		(ref) => !Object.keys(ziFields)
			.includes(ref.getAttribute('data-element'))
			|| !ref.value
			|| ref.value === '',
	);
	showFields(fieldsToShow);
};

/**
 * hides all elements except those that zi uses for search
 */
export const hideAllElementsExceptZoomInfoSearch = (formFields: AnyFormElement[]): void => {
	const zoomInfoSearchFields = formFields
		.filter((ref) => ref.tagName === 'FORMS-UI-INPUT')
		.filter((ref) => ['emailAddress', 'businessEmailAddress'].includes(ref.name));
	const allFormFields = formFields.filter((ref) => !zoomInfoSearchFields.includes(ref));

	showFields(zoomInfoSearchFields);
	hideFields([...allFormFields]);
};

/**
 * injects boilerplate zoomInfo script into page
 * previously "injectZoomInfo"
 */
export const injectZoomInfo = (formFieldRefs: AnyFormElement[], zoomInfoProjectKey: string) => {
	/** zoominfo is already loaded. abort. */
	if (document.getElementById('zoomInfoScript')) return;

	const ziScriptElement = document.createElement('script');
	ziScriptElement.setAttribute('id', 'zoomInfoScript');
	ziScriptElement.text = `ZIProjectKey = '${zoomInfoProjectKey}';
      var zi = document.createElement('script');
      zi.setAttribute('id', 'ziScript');
      window.ZIWhiteList = window.ZIWhiteList ? window.ZIWhiteList.concat('formcomplete') : ['formcomplete'];
      (zi.type = 'text/javascript'),
      (zi.async = true),
      (zi.src = 'https://js.zi-scripts.com/zi-tag.js'),
      document.readyState === 'complete'?
      document.body.appendChild(zi):
      window.addEventListener('load', function(){
      document.body.appendChild(zi)
      });`;

	document.head.append(ziScriptElement);

	if (!window._zi_fc) {
		window._zi_fc = {};
	}

	window._zi_fc.onMatch = (ziFields: any) => onMatch(ziFields, formFieldRefs);
};

/**
 * Removed ZoomInfo elements from page. Counter-action to injection
 */
export const removeZoomInfo = () => {
	window._zi_fc = {};
	const zoomInfoScriptRef = document.getElementById('zoomInfoScript');
	if (zoomInfoScriptRef) {
		zoomInfoScriptRef.remove();
	}
	const ziScriptRef = document.getElementById('ziScript');
	if (ziScriptRef) {
		ziScriptRef.remove();
	}
};

/** returns whether a field __can__ be a zoomInfo search field. Useful for determining whether to use a key */
export const isZoomInfoSearchField = (formField: FormField) => formField.control === 'Input'
    && formField.type === 'Email'
    && (formField.dataElement === 'emailAddress' || formField.dataElement === 'businessEmailAddress');
